import React, { useEffect } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { Container, CssBaseline } from '@material-ui/core';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import {
  compose,
  lifecycle,
  setPropTypes,
  withHandlers,
  withState
} from 'recompose';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import _ from 'lodash';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import { getAccounts } from '../../actions/Accounts';
import { isAuthenticated, sendLogin } from '../../auth/Auth';
import ProgressDialog from '../../components/molecules/ProgressDialog';
import { getGroupIdentities } from '../../actions/Identity';
import GroupBankInfoForm from '../organisms/GroupBankInfoForm';
import GroupIdentityInfoForm from '../organisms/GroupIdentityInfoForm';
import GroupInfoConfirm from '../organisms/GroupInfoConfirm';
import { getGroupDetail } from '../../actions/Groups';
import GroupIdentityInfoSubmitted from '../organisms/GroupIdentityInfoSubmitted';
import {
  getBankList,
  getBankBranchList,
  GET_BANK_BRANCH_LIST_SUCCESS,
  GET_BANK_LIST_SUCCESS
} from '../../actions/Bank';

const styles = () => ({
  '@global': {
    body: {
      backgroundColor: '#F7F7F7',
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
    }
  },
  main: {
    flexGrow: 1,
    maxWidth: 375,
    paddingLeft: 0,
    paddingRight: 0,
    paddingBottom: 20
    // border: '1px solid #ccc'
  }
});

const propTypes = {
  classes: PropTypes.object.isRequired
};

const enhance = compose(
  connect(
    state => ({
      account: state.accounts.account,
      reIdentity: state.accounts.reIdentity,
      cognitoUserId: state.cognitoUser.cognitoUserId
    }),
    dispatch => ({
      actions: bindActionCreators(
        {
          getAccounts,
          getGroupDetail,
          getGroupIdentities,
          getBankList,
          getBankBranchList
        },
        dispatch
      )
    })
  ),
  setPropTypes(propTypes),
  withState('openProgress', 'updateOpenProgress', false),
  withState('show', 'setShow', false),
  withState('step', 'setStep', '1.1'),
  withState('stepPreConfirm', 'setStepPreConfirm', '1.2'),
  withState('groupParams', 'setGroupParams', {}),
  withState('identityParams', 'setIdentityParams', {}),
  withState('identity', 'setIdentity', {}),
  withState('identityId', 'setIdentityId', 0),
  withState('listBank', 'setListBank', []),
  withState('listBankBranch', 'setListBankBranch', []),
  withHandlers({
    handleRedirect: props => path => {
      props.history.push(path);
    },
    checkSession: props => data => {
      if (data.error === true && data.payload.status === 403) {
        sendLogin(props);
      }
      return data;
    }
  }),
  lifecycle({
    componentDidMount() {
      isAuthenticated(this.props);
      this.props.updateOpenProgress(true);
      Promise.all([
        this.props.actions
          .getAccounts({
            cognitoUserId: this.props.cognitoUserId
              ? this.props.cognitoUserId
              : ''
          })
          .then(accountData => {
            this.props.checkSession(accountData);
            if (
              accountData.payload.groupAccountStatus &&
              accountData.payload.groupAccountStatus.group === true &&
              accountData.payload.groupAccountStatus.identityRegistered === true
            ) {
              this.props.setStep('3');
            }
            if (accountData.payload.groupId) {
              return this.props.actions.getGroupDetail(
                accountData.payload.groupId
              );
            }
            return null;
          }),
        this.props.actions
          .getGroupIdentities({
            page: 1,
            limit: 1
          })
          .then(identityData => {
            this.props.checkSession(identityData);
            const identities = _.get(identityData, 'payload.identities', []);
            if (identities.length > 0) {
              const identity = identities[0];
              if (identity) {
                this.props.setIdentity(identity);
                this.props.setIdentityId(_.get(identity, 'identityId', 0));
              }
            }
          }),
        this.props.actions.getBankList().then(data => {
          this.props.checkSession(data);
          if (data.type === GET_BANK_LIST_SUCCESS) {
            this.props.setListBank(data.payload);
          } else {
            this.props.setListBank([]);
          }
        }),
        this.props.actions.getBankBranchList().then(data => {
          if (data.type === GET_BANK_BRANCH_LIST_SUCCESS) {
            this.props.setListBankBranch(data.payload);
          } else {
            this.props.setListBankBranch([]);
          }
        })
      ]).then(() => {
        if (this.props.reIdentity) {
          this.props.setStep('1.2');
        }
        this.props.setShow(true);
        this.props.updateOpenProgress(false);
      });
    }
  })
);

const Main = enhance(
  ({
    classes,
    openProgress,
    updateOpenProgress,
    show,
    step,
    setStep,
    stepPreConfirm,
    setStepPreConfirm,
    groupParams,
    setGroupParams,
    identityParams,
    setIdentityParams,
    identityId,
    listBank,
    listBankBranch
  }) => {
    useEffect(() => {
      // Scroll to top when step changed
      window.scrollTo(0, 0);
    }, [step]);

    // Must use CSS display none because conditional rendering can not keep validation states
    return (
      <Container component="main" className={classes.main}>
        <CssBaseline />
        <PublicHeader />
        {show && (
          <>
            <GroupBankInfoForm
              show={step === '1.1'}
              listBank={listBank}
              listBankBranch={listBankBranch}
              proceedToNext={formData => {
                setGroupParams({ ...groupParams, ...formData.groupParams });
                if (formData.hasSpecialCode) {
                  // if has special code then only save identity params from step 1.1
                  setIdentityParams({
                    ...formData.identityParams
                  });
                  setStepPreConfirm('1.1');
                  setStep('2');
                } else {
                  setIdentityParams({
                    ...identityParams,
                    ...formData.identityParams
                  });
                  setStep('1.2');
                }
              }}
            />
            <GroupIdentityInfoForm
              show={step === '1.2'}
              proceedToNext={formData => {
                setIdentityParams({
                  ...identityParams,
                  ...formData.identityParams
                });
                setStepPreConfirm('1.2');
                setStep('2');
              }}
              handleBack={() => {
                setStep('1.1');
              }}
            />
            <GroupInfoConfirm
              show={step === '2'}
              groupParams={groupParams}
              identityParams={identityParams}
              identityId={identityId}
              proceedToNext={() => {
                setStep('3');
              }}
              handleBack={() => {
                setStep(stepPreConfirm);
              }}
            />
            <GroupIdentityInfoSubmitted
              show={step === '3'}
              updateOpenProgress={updateOpenProgress}
              listBank={listBank}
              listBankBranch={listBankBranch}
            />
          </>
        )}
        <PublicFooter />
        <ProgressDialog openProgress={openProgress} loading />
      </Container>
    );
  }
);

export default withRouter(withStyles(styles)(Main));
